import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Rocket, Shield, Server, Database, Globe, Key, CheckCircle2, XCircle,
  Loader2, ChevronRight, ChevronLeft, Download, GitBranch, Eye, EyeOff,
  Lock, Cloud, Network, MonitorCheck, Mail, HardDrive, FileCode2, Play,
  AlertTriangle, Copy, HelpCircle, Terminal, Search, DollarSign, RefreshCw,
  ArrowLeft, Sparkles, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "connect", label: "Azure Connection", icon: Key, desc: "Tenant, credentials & region" },
  { id: "configure", label: "Environment", icon: Server, desc: "Compute, security & services" },
  { id: "deploy", label: "Deploy / Export", icon: Rocket, desc: "Review, estimate & launch" },
];

const TIER_INFO = {
  appservice: {
    label: "App Service", subtitle: "Dev / Test",
    desc: "Cost-optimized. Single-instance with auto-scaling option.",
    cost: "~$13/mo (B1) to ~$73/mo (S1)", color: "text-blue-500",
  },
  aks: {
    label: "AKS Cluster", subtitle: "Staging / Production",
    desc: "Scale-ready. Multi-node Kubernetes with auto-scaling.",
    cost: "~$300+/mo (3-node D4s_v3)", color: "text-emerald-500",
  },
};

const SECURITY_OPTIONS = [
  { key: "key_vault", label: "Azure Key Vault", desc: "Store all secrets securely", icon: Lock, critical: true },
  { key: "private_endpoints", label: "Private Endpoints", desc: "No public access to DB/services", icon: Network, critical: true },
  { key: "managed_identity", label: "Managed Identity", desc: "Passwordless service auth", icon: Shield, critical: true },
  { key: "defender", label: "Defender for Cloud", desc: "Threat detection & posture", icon: MonitorCheck },
  { key: "waf", label: "WAF (App Gateway)", desc: "Web application firewall", icon: Shield, expensive: true },
  { key: "ddos_protection", label: "DDoS Protection", desc: "Standard DDoS mitigation (~$2,944/mo)", icon: Cloud, expensive: true },
  { key: "private_link_service", label: "Private Link Service", desc: "Allow customer Private Endpoints", icon: Network, enterprise: true },
];

const SERVICES = [
  { key: "cosmos_mongodb", label: "Cosmos DB (MongoDB)", desc: "Primary data store", icon: Database, required: true },
  { key: "cosmos_gremlin", label: "Cosmos DB (Gremlin)", desc: "Resource graph", icon: GitBranch },
  { key: "communication_services", label: "Communication Services", desc: "Email notifications", icon: Mail },
  { key: "app_insights", label: "Application Insights", desc: "Monitoring & alerting", icon: MonitorCheck },
  { key: "storage_account", label: "Storage Account", desc: "Backups & exports", icon: HardDrive },
];

export default function DeploymentWizardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [regions, setRegions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [preflighting, setPreflighting] = useState(false);

  // Help state
  const [helpData, setHelpData] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpQuery, setHelpQuery] = useState("");
  const [askingAi, setAskingAi] = useState(false);

  // Cost state
  const [costEstimate, setCostEstimate] = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  const [form, setForm] = useState({
    name: "", environment_type: "dev",
    tenant_id: "", subscription_id: "", client_id: "", client_secret: "",
    region: "eastus", resource_group_name: "rg-niyantproduction-control",
    compute_tier: "appservice", credential_storage: "keyvault",
    architecture: "two-plane", // "single" or "two-plane"
    security_options: { key_vault: true, private_endpoints: true, managed_identity: true, defender: true, waf: false, ddos_protection: false, private_link_service: false },
    services: { cosmos_mongodb: true, cosmos_gremlin: true, communication_services: true, app_insights: true, storage_account: true },
    git_provider: "", git_repo: "", git_branch: "main", git_pat: "",
  });

  useEffect(() => { api().get("/deployments/regions").then(r => setRegions(r.data.regions || [])).catch(() => {}); }, [api]);

  const updateForm = (field, value) => { setForm(prev => ({ ...prev, [field]: value })); setPreflight(null); };
  const updateSecurity = (key, value) => setForm(prev => ({ ...prev, security_options: { ...prev.security_options, [key]: value } }));
  const updateService = (key, value) => setForm(prev => ({ ...prev, services: { ...prev.services, [key]: value } }));

  const stepKeys = ["connect", "configure", "deploy"];

  // Load help (with optional query)
  const loadHelp = useCallback(async (query = null) => {
    if (query) setAskingAi(true); else setHelpLoading(true);
    try {
      const res = await api().post("/deployments/help", {
        step: stepKeys[step],
        error_context: preflight?.overall === "fail" ? JSON.stringify(preflight.checks) : null,
        user_query: query,
      });
      setHelpData(res.data);
    } catch { /* ignore */ } finally { setHelpLoading(false); setAskingAi(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, step, preflight]);

  // Auto-load help chips when step changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHelp(); }, [step]);

  const handleAskAi = () => {
    if (!helpQuery.trim()) return;
    loadHelp(helpQuery.trim());
    setHelpQuery("");
  };

  const handleChipClick = (query) => {
    setHelpQuery("");
    loadHelp(query);
  };

  // Cost estimator
  const loadCostEstimate = useCallback(async () => {
    setCostLoading(true);
    try {
      const res = await api().post("/deployments/estimate-cost", {
        compute_tier: form.compute_tier, environment_type: form.environment_type,
        region: form.region, security_options: form.security_options, services: form.services,
      });
      setCostEstimate(res.data);
    } catch { /* ignore */ } finally { setCostLoading(false); }
  }, [api, form.compute_tier, form.environment_type, form.region, form.security_options, form.services]);

  useEffect(() => { if (step >= 1) loadCostEstimate(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form.compute_tier, form.environment_type, form.security_options, form.services]);

  // Preflight
  const handlePreflight = async () => {
    if (!form.client_id || !form.client_secret) {
      setPreflight({ overall: "skip" }); setStep(1); return;
    }
    setPreflighting(true); setPreflight(null);
    try {
      const res = await api().post("/deployments/preflight", {
        tenant_id: form.tenant_id, subscription_id: form.subscription_id,
        client_id: form.client_id, client_secret: form.client_secret,
      });
      setPreflight(res.data);
      if (res.data.overall !== "fail") { toast.success("Azure connection validated"); setStep(1); }
      else toast.error("Validation failed. Check the results.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preflight check failed");
      setPreflight({ overall: "fail", checks: { connection: { status: "fail", message: err.response?.data?.detail || "Request failed" } } });
    } finally { setPreflighting(false); }
  };

  const [deployProgress, setDeployProgress] = useState("");
  const [deployError, setDeployError] = useState("");

  const handleValidate = async () => {
    setValidating(true);
    setDeployError("");
    try {
      // Step 1: Create deployment
      setDeployProgress("Creating deployment...");
      const createRes = await api().post("/deployments", buildPayload());
      const depId = createRes.data.id;

      // Step 2: Validate Azure connection
      setDeployProgress("Validating Azure connection...");
      const valRes = await api().post(`/deployments/${depId}/validate`);
      if (valRes.data.status !== "success") {
        const msg = valRes.data.message || "Validation failed";
        setDeployError(msg);
        toast.error(msg);
        await api().delete(`/deployments/${depId}`);
        setDeployProgress("");
        return;
      }
      toast.success("Azure connection validated");

      // Step 3: Generate templates
      setDeployProgress("Generating Bicep + ARM templates...");
      const genRes = await api().post(`/deployments/${depId}/generate`);
      toast.success(`Generated ${genRes.data.bicep_files.length} templates`);

      // Step 4: Execute deployment
      setDeployProgress("Submitting deployment to Azure...");
      const execRes = await api().post(`/deployments/${depId}/execute`);
      if (execRes.data.status === "error") {
        setDeployError(execRes.data.message);
        toast.error(execRes.data.message);
        setDeployProgress("");
      } else {
        setDeployProgress("Deployment submitted! Provisioning resources...");
        setValidated(true);
        toast.success(execRes.data.message);
        setTimeout(() => navigate("/deployments"), 2000);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Deployment failed";
      if (err.response?.status === 403) {
        toast.error("Session expired or permissions changed. Please log in again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }
      setDeployError(msg);
      toast.error(msg);
      setDeployProgress("");
    } finally { setValidating(false); }
  };

  const handleCreateDraft = async () => {
    setSaving(true);
    try {
      await api().post("/deployments", buildPayload());
      toast.success("Deployment saved as draft");
      setTimeout(() => navigate("/deployments"), 800);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const buildPayload = () => ({
    name: form.name, environment_type: form.environment_type,
    azure_config: { tenant_id: form.tenant_id, subscription_id: form.subscription_id, client_id: form.client_id, client_secret: form.client_secret, region: form.region, resource_group_name: form.resource_group_name },
    compute_tier: form.compute_tier, security_options: form.security_options,
    services: form.services, credential_storage: form.credential_storage,
    architecture: form.architecture,
    git_config: form.git_provider ? { provider: form.git_provider, repo_url: form.git_repo, branch: form.git_branch, pat: form.git_pat } : null,
  });

  const canNext = () => {
    if (step === 0) return form.name && form.tenant_id && form.subscription_id;
    return true;
  };

  return (
    <div className="animate-page-enter" data-testid="deployment-wizard-page">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/deployments")} className="text-xs text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]" data-testid="wizard-back-to-list">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Deployments
        </Button>
        <span className="text-[var(--n-border-3)]">/</span>
        <span className="text-sm font-medium">New Deployment</span>
      </div>

      {/* Mobile step indicator */}
      <div className="flex items-center gap-2 mb-4 lg:hidden overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap shrink-0 transition-all ${
                i === step ? "bg-[var(--hig-accent)] text-white font-medium" :
                i < step ? "bg-emerald-500/10 text-emerald-600" :
                "bg-[var(--n-bg)] text-[var(--n-text-muted)]"
              }`}
            >
              {i < step ? <CheckCircle2 className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
              {s.label}
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-[var(--n-text-muted)] shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_320px] gap-8">
        {/* Left sidebar — Step nav */}
        <div className="hidden lg:block" data-testid="wizard-steps-nav">
          <div className="lg:sticky lg:top-4 space-y-1">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => i < step && setStep(i)}
                className={`w-full text-left px-3 py-3 rounded-xl transition-all ${
                  i === step
                    ? "bg-[var(--hig-accent-subtle)] border border-[var(--hig-accent)]/20"
                    : i < step
                    ? "hover:bg-[var(--n-hover)] cursor-pointer"
                    : "opacity-40 cursor-default"
                }`}
                disabled={i > step}
                data-testid={`wizard-step-${s.id}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                    i < step ? "bg-emerald-500 text-white" :
                    i === step ? "bg-[var(--hig-accent)] text-white" :
                    "bg-[var(--n-border-2)] text-[var(--n-text-muted)]"
                  }`}>
                    {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <div>
                    <span className={`text-xs font-medium block ${i === step ? "text-[var(--hig-accent)]" : ""}`}>{s.label}</span>
                    <span className="text-[9px] text-[var(--n-text-muted)]">{s.desc}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Center — Main form */}
        <div className="min-h-[60vh]" data-testid="wizard-main-content">
          {step === 0 && <StepConnect form={form} updateForm={updateForm} regions={regions} showSecret={showSecret} setShowSecret={setShowSecret} preflight={preflight} />}
          {step === 1 && <StepConfigure form={form} updateForm={updateForm} updateSecurity={updateSecurity} updateService={updateService} />}
          {step === 2 && <StepDeploy form={form} updateForm={updateForm} regions={regions} costEstimate={costEstimate} costLoading={costLoading} loadCostEstimate={loadCostEstimate} validated={validated} deployProgress={deployProgress} deployError={deployError} />}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-[var(--n-border)]">
            <Button variant="ghost" onClick={() => step === 0 ? navigate("/deployments") : setStep(step - 1)} className="text-sm" data-testid="wizard-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> {step === 0 ? "Cancel" : "Back"}
            </Button>

            <div className="flex items-center gap-3">
              {step === 0 && (
                <Button onClick={handlePreflight} disabled={!canNext() || preflighting} className="text-sm bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent-hover)]" data-testid="wizard-next-btn">
                  {preflighting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Validating...</> : <>Continue <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              )}
              {step === 1 && (
                <Button onClick={() => setStep(2)} className="text-sm bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent-hover)]" data-testid="wizard-next-btn">
                  Review & Deploy <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 2 && (
                <div className="flex items-center gap-2">
                  <Button onClick={handleCreateDraft} disabled={saving} variant="outline" className="text-sm" data-testid="save-draft-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />} Save Draft
                  </Button>
                  <Button onClick={handleValidate} disabled={validating || !form.client_id || !form.client_secret} className="text-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid="validate-deploy-btn">
                    {validating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Rocket className="w-4 h-4 mr-1.5" />} {validating ? "Deploying..." : "Deploy to Azure"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right panel — Cost + AI (desktop) */}
        <div className="hidden lg:block" data-testid="wizard-context-panel">
          <div className="sticky top-4 space-y-4">
            <CostPanel costEstimate={costEstimate} costLoading={costLoading} loadCostEstimate={loadCostEstimate} step={step} />
            <HelpPanelInline helpData={helpData} helpQuery={helpQuery} setHelpQuery={setHelpQuery} handleAskAi={handleAskAi} askingAi={askingAi} handleChipClick={handleChipClick} />
          </div>
        </div>

        {/* Mobile: Cost + AI below form */}
        <div className="lg:hidden space-y-4">
          <CostPanel costEstimate={costEstimate} costLoading={costLoading} loadCostEstimate={loadCostEstimate} step={step} />
          <HelpPanelInline helpData={helpData} helpQuery={helpQuery} setHelpQuery={setHelpQuery} handleAskAi={handleAskAi} askingAi={askingAi} handleChipClick={handleChipClick} />
        </div>
      </div>
    </div>
  );
}

/* ============ Step Components ============ */

function StepConnect({ form, updateForm, regions, showSecret, setShowSecret, preflight }) {
  return (
    <div className="space-y-6" data-testid="step-connect">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Azure Connection</h2>
        <p className="text-sm text-[var(--n-text-muted)] mt-1">Connect to your Azure tenant with a Service Principal.</p>
      </div>

      <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Deployment Name *</Label>
              <Input value={form.name} onChange={e => updateForm("name", e.target.value)} placeholder="e.g. niyant-prod" className="bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid="input-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Environment</Label>
              <Select value={form.environment_type} onValueChange={v => updateForm("environment_type", v)}>
                <SelectTrigger className="bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid="select-env-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="dev">Development</SelectItem><SelectItem value="staging">Staging</SelectItem><SelectItem value="production">Production</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Tenant ID *</Label>
              <Input value={form.tenant_id} onChange={e => updateForm("tenant_id", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm" data-testid="input-tenant" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Subscription ID *</Label>
              <Input value={form.subscription_id} onChange={e => updateForm("subscription_id", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm" data-testid="input-subscription" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Client ID <span className="text-[var(--n-text-muted)] font-normal">(optional)</span></Label>
              <Input value={form.client_id} onChange={e => updateForm("client_id", e.target.value)} placeholder="Service Principal App ID" className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm" data-testid="input-client-id" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Client Secret <span className="text-[var(--n-text-muted)] font-normal">(optional)</span></Label>
              <div className="relative">
                <Input type={showSecret ? "text" : "password"} value={form.client_secret} onChange={e => updateForm("client_secret", e.target.value)} placeholder="Service Principal secret" className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm pr-9" data-testid="input-client-secret" />
                <button onClick={() => setShowSecret(!showSecret)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--n-text-muted)]">
                  {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Region</Label>
              <Select value={form.region} onValueChange={v => updateForm("region", v)}>
                <SelectTrigger className="bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid="select-region"><SelectValue /></SelectTrigger>
                <SelectContent>{regions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Resource Group</Label>
              <Input value={form.resource_group_name} onChange={e => updateForm("resource_group_name", e.target.value)} className="bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid="input-rg" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credential Storage */}
      <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium">Credential Storage Policy</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "keyvault", title: "Store in Key Vault", desc: "Encrypted in deployed Key Vault. Required for drift monitoring." },
              { value: "none", title: "Never Store", desc: "Used once during deployment, then discarded." },
            ].map(opt => (
              <label key={opt.value} className={`cursor-pointer p-3.5 rounded-xl border-2 transition-all ${form.credential_storage === opt.value ? "border-[var(--hig-accent)] bg-[var(--hig-accent-subtle)]" : "border-[var(--n-border-2)] hover:border-[var(--n-border-3)]"}`}>
                <input type="radio" className="sr-only" checked={form.credential_storage === opt.value} onChange={() => updateForm("credential_storage", opt.value)} />
                <div className="text-sm font-medium mb-0.5">{opt.title}</div>
                <div className="text-xs text-[var(--n-text-muted)]">{opt.desc}</div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preflight results */}
      {preflight && preflight.overall !== "skip" && <PreflightResults preflight={preflight} />}
    </div>
  );
}

function StepConfigure({ form, updateForm, updateSecurity, updateService }) {
  const ARCHITECTURE_INFO = {
    "two-plane": {
      label: "Two-Plane Architecture",
      subtitle: "Recommended",
      desc: "Separate Control & Data planes. Safe redeployments without data loss.",
      features: ["Control Plane: App Service, SWA, ACS, Key Vault", "Data Plane: Cosmos DB, Log Analytics", "VNet peering for secure communication"],
      color: "text-emerald-500",
    },
    "single": {
      label: "Single Resource Group",
      subtitle: "Legacy",
      desc: "All resources in one group. Simpler but riskier for redeployments.",
      features: ["All resources in one RG", "Simpler setup", "Full cleanup on redeploy"],
      color: "text-blue-500",
    },
  };

  return (
    <div className="space-y-6" data-testid="step-configure">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Environment Configuration</h2>
        <p className="text-sm text-[var(--n-text-muted)] mt-1">Select architecture, compute tier, security controls, and services.</p>
      </div>

      {/* Architecture Selection */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Deployment Architecture</Label>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(ARCHITECTURE_INFO).map(([key, info]) => (
            <label key={key} className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${form.architecture === key ? "border-[var(--hig-accent)] bg-[var(--hig-accent-subtle)]" : "border-[var(--n-border-2)] hover:border-[var(--n-border-3)]"}`} data-testid={`arch-${key}`}>
              <input type="radio" className="sr-only" checked={form.architecture === key} onChange={() => updateForm("architecture", key)} />
              <div className="flex items-center gap-2 mb-1.5">
                <Network className={`w-4 h-4 ${info.color}`} />
                <span className="text-sm font-medium">{info.label}</span>
                <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${key === "two-plane" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" : ""}`}>{info.subtitle}</Badge>
              </div>
              <p className="text-xs text-[var(--n-text-muted)] mb-2">{info.desc}</p>
              <ul className="space-y-1">
                {info.features.map((f, i) => (
                  <li key={i} className="text-[10px] text-[var(--n-text-muted)] flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </label>
          ))}
        </div>
        {form.architecture === "two-plane" && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
            <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-start gap-2">
              <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Two-Plane creates separate resource groups: <code className="font-mono">rg-{form.name || "{name}"}-control</code> and <code className="font-mono">rg-{form.name || "{name}"}-data</code>. You can safely redeploy the Control Plane without touching your data.</span>
            </p>
          </div>
        )}
      </div>

      {/* Compute Tier */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Compute Tier</Label>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(TIER_INFO).map(([key, info]) => (
            <label key={key} className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${form.compute_tier === key ? "border-[var(--hig-accent)] bg-[var(--hig-accent-subtle)]" : "border-[var(--n-border-2)] hover:border-[var(--n-border-3)]"}`}>
              <input type="radio" className="sr-only" checked={form.compute_tier === key} onChange={() => updateForm("compute_tier", key)} />
              <div className="flex items-center gap-2 mb-1.5">
                <Server className={`w-4 h-4 ${info.color}`} />
                <span className="text-sm font-medium">{info.label}</span>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0">{info.subtitle}</Badge>
              </div>
              <p className="text-xs text-[var(--n-text-muted)]">{info.desc}</p>
              <p className="text-[10px] text-[var(--n-text-muted)] mt-1 font-mono">{info.cost}</p>
            </label>
          ))}
        </div>
      </div>

      {/* Security */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Security Controls <span className="text-[var(--n-text-muted)] font-normal text-xs">(critical options recommended ON)</span></Label>
        <div className="grid grid-cols-2 gap-3">
          {SECURITY_OPTIONS.map(opt => (
            <div key={opt.key} className={`flex items-center justify-between p-3 rounded-xl border bg-[var(--n-surface)] ${
              opt.expensive && form.security_options[opt.key] ? "border-amber-500/50" : 
              opt.enterprise ? "border-purple-500/30" : "border-[var(--n-border)]"
            }`}>
              <div className="flex items-center gap-2.5">
                <opt.icon className={`w-4 h-4 ${opt.expensive ? "text-amber-500" : opt.enterprise ? "text-purple-500" : "text-[var(--hig-accent)]"}`} />
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium">{opt.label}</span>
                    {opt.expensive && <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium">HIGH COST</span>}
                    {opt.enterprise && <span className="text-[8px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-600 font-medium">ENTERPRISE</span>}
                  </div>
                  <span className="text-[10px] text-[var(--n-text-muted)]">{opt.desc}</span>
                </div>
              </div>
              <Switch checked={form.security_options[opt.key]} onCheckedChange={v => updateSecurity(opt.key, v)} data-testid={`security-${opt.key}`} />
            </div>
          ))}
        </div>
        {(form.security_options.waf || form.security_options.ddos_protection) && (
          <div className="mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>Cost Warning:</strong> WAF (~$263/mo) and DDoS Protection (~$2,944/mo) add significant monthly costs. These can be enabled later via the "Modify Deployment" feature after initial deployment.</span>
            </p>
          </div>
        )}
        {form.security_options.private_link_service && (
          <div className="mt-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
            <p className="text-xs text-purple-600 dark:text-purple-400 flex items-start gap-2">
              <Network className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>Private Link Service:</strong> Enables enterprise customers to connect their Data Plane to your Control Plane via Private Endpoints. Creates an additional <code className="font-mono">pls-subnet</code> in your VNet.</span>
            </p>
          </div>
        )}
      </div>

      {/* Services */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Services</Label>
        <div className="grid grid-cols-2 gap-3">
          {SERVICES.map(svc => (
            <div key={svc.key} className="flex items-center justify-between p-3 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)]">
              <div className="flex items-center gap-2.5">
                <svc.icon className="w-4 h-4 text-[var(--hig-accent)]" />
                <div>
                  <span className="text-xs font-medium block">{svc.label}</span>
                  <span className="text-[10px] text-[var(--n-text-muted)]">{svc.desc}</span>
                </div>
              </div>
              <Switch checked={form.services[svc.key]} onCheckedChange={v => updateService(svc.key, v)} disabled={svc.required} data-testid={`service-${svc.key}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepDeploy({ form, updateForm, regions, costEstimate, costLoading, loadCostEstimate, validated, deployProgress, deployError }) {
  const regionLabel = regions.find(r => r.value === form.region)?.label || form.region;
  return (
    <div className="space-y-6" data-testid="step-deploy">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Review & Deploy</h2>
        <p className="text-sm text-[var(--n-text-muted)] mt-1">Review your configuration and choose how to provision.</p>
      </div>

      {/* Summary */}
      <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
        <CardContent className="p-5">
          <h3 className="text-sm font-medium mb-3">Deployment Summary</h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div><span className="text-[var(--n-text-muted)]">Name:</span> <span className="font-medium">{form.name || "—"}</span></div>
            <div><span className="text-[var(--n-text-muted)]">Environment:</span> <span className="font-medium">{form.environment_type}</span></div>
            <div><span className="text-[var(--n-text-muted)]">Compute:</span> <span className="font-medium">{TIER_INFO[form.compute_tier]?.label}</span></div>
            <div><span className="text-[var(--n-text-muted)]">Region:</span> <span className="font-medium">{regionLabel}</span></div>
            <div><span className="text-[var(--n-text-muted)]">Security:</span> <span className="font-medium">{Object.values(form.security_options).filter(Boolean).length}/6 enabled</span></div>
            <div><span className="text-[var(--n-text-muted)]">Services:</span> <span className="font-medium">{Object.values(form.services).filter(Boolean).length}/5 enabled</span></div>
            <div><span className="text-[var(--n-text-muted)]">Credentials:</span> <span className="font-medium">{form.credential_storage === "keyvault" ? "Key Vault" : "Not stored"}</span></div>
            <div><span className="text-[var(--n-text-muted)]">Resource Group:</span> <span className="font-mono font-medium">{form.resource_group_name}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Git Config */}
      <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <GitBranch className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-sm font-medium">Git Integration</span>
            <Badge variant="outline" className="text-[9px]">Optional</Badge>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Provider</Label>
              <Select value={form.git_provider || "none"} onValueChange={v => updateForm("git_provider", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid="select-git-provider"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="azure_devops">Azure DevOps</SelectItem><SelectItem value="github">GitHub</SelectItem></SelectContent>
              </Select>
            </div>
            {form.git_provider && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Repository URL</Label>
                  <Input value={form.git_repo} onChange={e => updateForm("git_repo", e.target.value)} placeholder="https://..." className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm" data-testid="input-git-repo" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Branch</Label>
                  <Input value={form.git_branch} onChange={e => updateForm("git_branch", e.target.value)} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm" data-testid="input-git-branch" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">PAT (Personal Access Token)</Label>
                  <Input type="password" value={form.git_pat} onChange={e => updateForm("git_pat", e.target.value)} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm" data-testid="input-git-pat" />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Deployment Progress */}
      {deployProgress && !validated && (
        <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20" data-testid="deploy-progress">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-400">{deployProgress}</p>
              <p className="text-xs text-blue-600/70 dark:text-blue-500/70 mt-0.5">This may take a few moments...</p>
            </div>
          </div>
        </div>
      )}

      {/* Deployment Error */}
      {deployError && (
        <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20" data-testid="deploy-error">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">Deployment Failed</p>
                <pre className="text-xs text-red-600/80 dark:text-red-400/80 mt-1.5 whitespace-pre-wrap break-all font-mono bg-red-500/5 p-3 rounded-lg max-h-40 overflow-y-auto">{deployError}</pre>
              </div>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(deployError); toast.success("Error copied to clipboard"); }}
              className="shrink-0 p-1.5 rounded-lg hover:bg-red-500/10 text-red-400"
              title="Copy error"
              data-testid="copy-error-btn"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {validated && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center" data-testid="validation-success">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Deployment submitted to Azure!</p>
          <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">Your infrastructure is being provisioned. Redirecting to deployments...</p>
        </div>
      )}
    </div>
  );
}

/* ============ Shared Components ============ */

function PreflightResults({ preflight }) {
  const checks = ["connection", "contributor", "user_access_admin", "resource_provider"];
  const labels = { connection: "Azure Connectivity", contributor: "Contributor Role", user_access_admin: "User Access Administrator", resource_provider: "Resource Providers" };
  const icon = (s) => s === "pass" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : s === "warn" ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-red-500" />;

  return (
    <Card className="border-[var(--n-border)]" data-testid="preflight-results">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[var(--hig-accent)]" />
          <span className="text-sm font-medium">Permission Validation</span>
          <Badge className={`text-[9px] px-1.5 py-0 ${preflight.overall === "pass" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
            {preflight.overall === "pass" ? "All Passed" : "Failed"}
          </Badge>
        </div>
        {checks.map(key => {
          const c = preflight.checks?.[key];
          if (!c) return null;
          return (
            <div key={key} className="flex items-start gap-3" data-testid={`preflight-${key}`}>
              {icon(c.status)}
              <div>
                <span className="text-xs font-medium block">{labels[key]}</span>
                <span className="text-[11px] text-[var(--n-text-muted)]">{c.message}</span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function CostPanel({ costEstimate, costLoading, loadCostEstimate, step }) {
  if (step < 1) return null;
  return (
    <Card className="bg-[var(--n-surface)] border-[var(--n-border)] overflow-hidden" data-testid="cost-estimator">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-medium">Estimated Monthly Cost</span>
          </div>
          <Button size="sm" variant="ghost" onClick={loadCostEstimate} disabled={costLoading} className="h-7 w-7 p-0" data-testid="refresh-cost-btn">
            {costLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        {costLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>
        ) : costEstimate ? (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
              <span className="text-3xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="cost-total-monthly">${costEstimate.total_monthly.toLocaleString()}</span>
              <span className="text-sm text-[var(--n-text-muted)] ml-1">/mo</span>
              <div className="text-xs text-[var(--n-text-muted)] mt-1" data-testid="cost-total-annual">~${costEstimate.total_annual.toLocaleString()}/year</div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {costEstimate.line_items.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-xs" data-testid={`cost-line-${i}`}>
                  <span className="text-[var(--n-text-secondary)] truncate pr-3 min-w-0">{item.resource}</span>
                  <span className={`font-mono tabular-nums shrink-0 font-medium ${item.monthly === 0 ? "text-emerald-500" : ""}`}>
                    {item.monthly === 0 ? "Free" : `$${item.monthly.toLocaleString()}`}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[var(--n-text-muted)] italic leading-relaxed pt-2 border-t border-[var(--n-border)]">{costEstimate.disclaimer}</p>
          </div>
        ) : (
          <p className="text-sm text-[var(--n-text-muted)] text-center py-6">Configure your environment to see costs.</p>
        )}
      </CardContent>
    </Card>
  );
}

function HelpPanelInline({ helpData, helpQuery, setHelpQuery, handleAskAi, askingAi, handleChipClick }) {
  if (!helpData) return null;
  return (
    <Card className="bg-[var(--n-surface)] border-[var(--n-border)]" data-testid="help-panel-inline">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[var(--hig-accent)]" />
          <span className="text-sm font-medium">AI Assistant</span>
        </div>
        <div className="flex gap-2">
          <Input
            value={helpQuery}
            onChange={e => setHelpQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAskAi()}
            placeholder="Ask about this step..."
            className="h-9 text-sm bg-[var(--n-bg)] border-[var(--n-border-2)] flex-1 min-w-0"
            data-testid="help-query-input"
          />
          <Button size="sm" onClick={handleAskAi} disabled={askingAi || !helpQuery.trim()} className="h-9 w-9 p-0 shrink-0 bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent-hover)]" data-testid="help-ask-btn">
            {askingAi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
        </div>
        {helpData.chips?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {helpData.chips.slice(0, 3).map((chip, i) => (
              <button key={i} onClick={() => handleChipClick(chip.query)} className="px-2.5 py-1 text-[11px] rounded-full border border-[var(--n-border-2)] hover:border-[var(--hig-accent)] hover:text-[var(--hig-accent)] hover:bg-[var(--hig-accent-subtle)] transition-all" data-testid={`help-chip-sheet-${i}`}>
                {chip.label}
              </button>
            ))}
          </div>
        )}
        {helpData.ai_guidance && (
          <div className="p-3 rounded-xl bg-[var(--hig-accent-subtle)] border border-[var(--hig-accent)]/10">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3 text-[var(--hig-accent)]" />
              <span className="text-[10px] font-medium text-[var(--hig-accent)]">AI Guidance</span>
            </div>
            <p className="text-xs text-[var(--n-text-secondary)] whitespace-pre-line leading-relaxed">{helpData.ai_guidance}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
